import { Collapse, Grid, Icon, Typography } from '@material-ui/core';
import type { StyledComponentProps } from '@material-ui/core/styles';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import ColoredFormattedNumberForeign from 'components/ColoredFormattedNumberForeign';
import MyTable, { MyTableBody, MyTableCol, MyTableHeader, MyTableRow } from 'components/MyTable';
import Spin from 'components/Spin';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { loadExtractItems } from 'store/Orbita/Financial/actions';
import { Export } from 'v2/components/Pages/Financial/AvailableTable/Export';
import FinancialTour from 'v2/components/Shared/Tour';

import { financialTourSteps } from '../../tour';
import styles from '../styles';

import type { FilterBy } from '../..';
import type IPagination from 'interfaces/IPagination';
import type { DispatchProp } from 'react-redux';

interface IProps extends DispatchProp<any>, StyledComponentProps {
  extract: any;
  filters: any;
  items: any;
  pagination: any;
  isFetching: boolean;
  canLoadMore: boolean;
  filterBy: FilterBy;
}

interface IStates {
  open: any;
  date: any;
  page: number;
  data: any[];
  itemValue: any;
}

class AvaliableTable extends Component<IProps, IStates> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      open: false,
      date: null,
      page: 1,
      data: [],
      itemValue: {
        isFetching: true,
      },
    };
  }

  handleClick = (id: any, date: any) => {
    const dateValue = date;

    if (id === this.state.open) {
      return this.setState({ open: null });
    }

    this.setState({
      open: id,
      date: dateValue,
    });

    if (date && date !== this.state.date) {
      this.setState({
        itemValue: {
          isFetching: true,
        },
      });

      (async () => {
        try {
          await this.props.dispatch(loadExtractItems(moment(date).format('YYYY-MM-DD')));

          this.setState({
            itemValue: this.props.items,
          });
        } catch (error) {
          toast.error(error);
        }
      })();
    }
  };

  componentDidUpdate(prevProps: IProps) {
    const { extract, pagination } = this.props;
    let data = this.state.data;

    if (prevProps.extract !== extract) {
      if (extract !== data) {
        if (pagination?.current_page === 1) {
          data = extract;
          this.setState({ page: 1 });
        } else {
          data = data.concat(extract);
        }

        this.setState({
          data,
          open: false,
        });
      }
    }
  }

  render() {
    const { classes, isFetching, pagination, filterBy } = this.props;
    const { itemValue } = this.state;
    const extract = this.state.data;

    const indexes = [
      { title: 'Data', ordering: false },
      { title: 'Descrição', ordering: false },
      { title: 'Valor', ordering: false, numeric: true },
      { title: 'Saldo', ordering: false, numeric: true },
      { title: '', ordering: false },
    ];

    const indexesCollapse = [
      { title: 'Descrição', ordering: false },
      { title: 'Valor', ordering: false, numeric: true },
    ];

    return (
      <div className={classes.wrapper}>
        <Grid container alignItems='center' justify='space-between' className={classes.tableTitle}>
          <Grid item>
            <Typography id='available-current-title' className={classes.tableTitleText}>
              Disponível / Atual
              {!isFetching && !!extract?.length && ` (${extract?.[0]?.currency.code})`}
            </Typography>
          </Grid>
          <Grid item>
            <Export filterBy={filterBy} itemsCount={extract?.length} />
          </Grid>
        </Grid>

        <div className={classes.scroll}>
          {!isFetching && !!extract?.length && (
            <div className={classes.tablePagination}>
              <span className='total'>
                Exibindo: 1-{extract.length} de {pagination?.total_items} item(s)
              </span>
            </div>
          )}

          {isFetching && (
            <div style={{ width: '100%', textAlign: 'center' }}>
              <Spin />
            </div>
          )}

          {!isFetching && extract && (
            <Fragment>
              <MyTable>
                <MyTableHeader index={indexes} />

                <MyTableBody>
                  {<FinancialTour name='financial_tour' steps={financialTourSteps} disableScrolling />}

                  {!extract.length && (
                    <MyTableRow>
                      <MyTableCol colSpan={5} screenSizes={{ xs: 12, sm: 12 }}>
                        Nenhum registro encontrado
                      </MyTableCol>
                    </MyTableRow>
                  )}
                  {extract
                    .slice(0)
                    .reverse()
                    .map((item: any, index: any) => {
                      return (
                        <Fragment key={index}>
                          <MyTableRow hover={true} onClick={() => this.handleClick(index, item.date)}>
                            <MyTableCol screenSizes={{ xs: 12, sm: 2 }}>{moment(item.date).format('L')}</MyTableCol>
                            <MyTableCol screenSizes={{ xs: 12, sm: 5 }}>Créditos/débitos como Produtor/Afiliado</MyTableCol>
                            <MyTableCol numeric screenSizes={{ xs: 6, sm: 2 }}>
                              <ColoredFormattedNumber value={item.daily_balance} format={item.currency.code} />
                              <ColoredFormattedNumberForeign value={item.daily_balance_origin_currency} separator=' / ' />
                            </MyTableCol>
                            <MyTableCol numeric screenSizes={{ xs: 6, sm: 2 }}>
                              <ColoredFormattedNumber value={item.balance} format={item.currency.code} />
                              <ColoredFormattedNumberForeign value={item.balance_origin} separator=' / ' />
                            </MyTableCol>
                            <MyTableCol numeric action screenSizes={{ xs: 12, sm: 1 }} style={{ cursor: 'pointer' }}>
                              {this.state.open === index ? <Icon>expand_less</Icon> : <Icon>expand_more</Icon>}
                            </MyTableCol>
                          </MyTableRow>

                          {this.state.open === index && (
                            <MyTableRow style={{ padding: 0 }}>
                              <MyTableCol colSpan={5} style={{ padding: 0 }} screenSizes={{ xs: 12, sm: 12 }}>
                                <Collapse in={true}>
                                  <MyTable>
                                    <MyTableHeader index={indexesCollapse} />

                                    <MyTableBody>
                                      {itemValue?.isFetching && (
                                        <MyTableRow>
                                          <MyTableCol colSpan={2} action screenSizes={{ xs: 12, sm: 12 }}>
                                            <div style={{ width: '100%', textAlign: 'center' }}>
                                              <Spin />
                                            </div>
                                          </MyTableCol>
                                        </MyTableRow>
                                      )}

                                      {!itemValue?.length && !itemValue?.isFetching && (
                                        <MyTableRow>
                                          <MyTableCol colSpan={2} screenSizes={{ xs: 12, sm: 12 }}>
                                            Nenhum resultado encontrado
                                          </MyTableCol>
                                        </MyTableRow>
                                      )}

                                      {!itemValue?.isFetching &&
                                        itemValue?.map((item: any, index: number) => {
                                          return (
                                            <MyTableRow key={item.id}>
                                              <MyTableCol
                                                style={{
                                                  borderRadius: index === itemValue.length - 1 ? '' : 0,
                                                  borderBottom: index === itemValue.length - 1 ? '0' : '1px solid #e2e2e2',
                                                }}
                                                screenSizes={{ xs: 12, sm: 9 }}
                                              >
                                                {item.description}
                                              </MyTableCol>
                                              <MyTableCol
                                                numeric
                                                style={{
                                                  borderRadius: index === itemValue.length - 1 ? '' : 0,
                                                  borderBottom: index === itemValue.length - 1 ? '0' : '1px solid #e2e2e2',
                                                }}
                                                screenSizes={{ xs: 12, sm: 3 }}
                                              >
                                                <ColoredFormattedNumber value={item.value} format='BRL' />
                                                <ColoredFormattedNumberForeign value={item.value_origin_currency} separator=' / ' />
                                              </MyTableCol>
                                            </MyTableRow>
                                          );
                                        })}
                                    </MyTableBody>
                                  </MyTable>
                                </Collapse>
                              </MyTableCol>
                            </MyTableRow>
                          )}
                        </Fragment>
                      );
                    })}
                </MyTableBody>
              </MyTable>
              <div style={{ textAlign: 'right', padding: '0.5rem 0', fontSize: '14px' }}>
                Total disponível: <ColoredFormattedNumber format='BRL' value={extract?.slice(0).reverse()[extract?.length - 1]?.balance || 0} />
              </div>
              <div style={{ textAlign: 'right', padding: '0.5rem 0', fontSize: '14px' }}>
                <ColoredFormattedNumberForeign
                  value={extract?.slice(0).reverse()[extract?.length - 1]?.balance_origin || 0}
                  separator='Total disponível: '
                />
              </div>
            </Fragment>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: any) {
  const canLoadMore: boolean = (() => {
    const pagination: IPagination = state.orbita.Financial.myExtract.pagination;
    if (pagination) {
      return pagination.per_page * pagination.current_page < pagination.total_items;
    }
    return false;
  })();
  return {
    extract: state.orbita.Financial.myExtract.data,
    isFetching: state.orbita.Financial.myExtract.isFetching || state.orbita.Financial.loadFilterByMonth.isFetching,
    pagination: state.orbita.Financial.myExtract.pagination,
    items: state.orbita.Financial.extractItems,
    filters: state.components.ExtractFilter.extractFilter,
    canLoadMore,
  };
}

export default connect(mapStateToProps)(styles(AvaliableTable));
